import {
  Button,
  Combobox,
  ComboboxOption,
  FieldWrap,
  Loader,
  Offscreen,
  Pagination,
  TextField
} from '@deque/cauldron-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SecurityLogExportsTable from '~/app/components/SecurityEventLog/SecurityLogExportsTable';
import { useSecurityLogExports } from '~/app/hooks/useSecurityLogExports';
import { type HttpError } from '~/common/api-client';
import { useAuthContext } from '~/common/contexts/auth';
import { useAllEnterprises } from '~/common/hooks/useAllEnterprises';
import {
  createSecurityEventLogExport,
  deleteSecurityEventLogExport,
  downloadSecurityEventLogExport,
  securityEventLogExportFilename,
  type SecurityEventLogExport
} from '~/common/utils/admin-client/get-security-event-log-exports';
import ContentToast from '~/modules/common/components/ContentToast';
import styles from './SecurityLogExportsTab.css';

// Matches the backend's default page size for the exports list.
const ITEMS_PER_PAGE = 25;

const toError = (err: unknown): Error =>
  err instanceof Error ? err : new Error(String(err));

const SecurityLogExportsTab: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const token = user?.token ?? '';

  const {
    data: enterprises,
    loading: loadingEnterprises,
    error: enterprisesError
  } = useAllEnterprises({ token });

  const [selectedEnterprise, setSelectedEnterprise] = useState<string | null>(
    null
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [createInProgress, setCreateInProgress] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [errorDismissed, setErrorDismissed] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const {
    exports,
    totalItems,
    loading,
    initialLoading,
    error,
    notConfigured,
    refresh
  } = useSecurityLogExports({
    token,
    enterpriseId: selectedEnterprise,
    page: currentPage,
    per_page: ITEMS_PER_PAGE
  });

  // Reset the dismissed flag whenever a new error arrives so a fresh toast
  // is shown — the hook returns a new Error instance on each failed fetch.
  useEffect(() => {
    if (error) {
      setErrorDismissed(false);
    }
  }, [error]);

  // Announce poll-driven status transitions politely so screen reader users
  // aren't left on a stale "Pending" while an export completes or fails.
  const statusesRef = useRef<Map<string, SecurityEventLogExport['status']>>(
    new Map()
  );
  useEffect(() => {
    const previous = statusesRef.current;
    const next = new Map<string, SecurityEventLogExport['status']>();
    const messages: string[] = [];
    for (const exp of exports) {
      next.set(exp.exportId, exp.status);
      const was = previous.get(exp.exportId);
      if (was && was !== exp.status) {
        if (exp.status === 'completed') {
          messages.push(
            t('Export for {{startDate}} to {{endDate}} completed.', {
              startDate: exp.startDate,
              endDate: exp.endDate
            })
          );
        } else if (exp.status === 'failed') {
          messages.push(
            t('Export for {{startDate}} to {{endDate}} failed.', {
              startDate: exp.startDate,
              endDate: exp.endDate
            })
          );
        }
      }
    }
    statusesRef.current = next;
    if (messages.length > 0) {
      setAnnouncement(messages.join(' '));
    }
  }, [exports, t]);

  const selectEnterprise = (enterpriseId: string) => {
    setSelectedEnterprise(enterpriseId);
    setCurrentPage(1);
  };

  const validateDates = (): boolean => {
    let valid = true;
    if (!startDate) {
      setStartDateError(t('Start date is required'));
      valid = false;
    } else {
      setStartDateError(null);
    }
    if (!endDate) {
      setEndDateError(t('End date is required'));
      valid = false;
    } else if (startDate && endDate < startDate) {
      setEndDateError(t('End date must be on or after the start date'));
      valid = false;
    } else {
      setEndDateError(null);
    }
    return valid;
  };

  const handleCreate = async () => {
    if (!selectedEnterprise || !validateDates()) {
      return;
    }
    setCreateInProgress(true);
    setActionError(null);
    try {
      await createSecurityEventLogExport({
        token,
        enterpriseId: selectedEnterprise,
        start_date: startDate,
        end_date: endDate
      });
      // The new row is newest-first on page 1; moving pages retriggers the
      // list, otherwise refresh in place (polling picks it up from there).
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        refresh();
      }
    } catch (err) {
      setActionError(toError(err));
    } finally {
      setCreateInProgress(false);
    }
  };

  const handleDownload = async (exp: SecurityEventLogExport) => {
    setActionError(null);
    try {
      await downloadSecurityEventLogExport({
        token,
        enterpriseId: exp.enterpriseId,
        exportId: exp.exportId,
        filename: securityEventLogExportFilename(exp)
      });
    } catch (err) {
      // 410: the storage lifecycle already deleted the file. The raw download
      // request can't carry the backend's message, so surface a translated
      // one, and refresh so the list reaps the stale row instead of offering
      // the download again.
      if ((err as HttpError).status === 410) {
        setActionError(new Error(t('The export file has expired.')));
        refresh();
      } else {
        setActionError(toError(err));
      }
    }
  };

  const handleDelete = async (exp: SecurityEventLogExport) => {
    setActionError(null);
    try {
      await deleteSecurityEventLogExport({
        token,
        enterpriseId: exp.enterpriseId,
        exportId: exp.exportId
      });
      refresh();
    } catch (err) {
      setActionError(toError(err));
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const rangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const rangeEnd = Math.min(currentPage * ITEMS_PER_PAGE, totalItems);

  // Deleting the last row of a trailing page (or server-side pruning) can
  // leave currentPage past the end; snap back to the last page that has rows.
  useEffect(() => {
    if (!loading && exports.length === 0 && totalItems > 0 && currentPage > 1) {
      setCurrentPage(totalPages);
    }
  }, [loading, exports.length, totalItems, currentPage, totalPages]);

  return (
    <>
      <Offscreen role="status" aria-live="polite">
        {announcement}
      </Offscreen>

      {enterprisesError && (
        <ContentToast type="caution" show>
          <strong>{t('Error loading enterprises -')}</strong>
          <code>{enterprisesError.message}</code>
        </ContentToast>
      )}

      {loadingEnterprises ? (
        <Loader label={t('Loading enterprises...')} tabIndex={-1} />
      ) : (
        <FieldWrap className={styles.fieldWrap}>
          <Combobox
            label={t('Search for an enterprise')}
            name="exports-enterprise-search"
            required
            requiredText={t('Required')}
          >
            {enterprises?.map(enterprise => (
              <ComboboxOption
                key={enterprise.id}
                value={enterprise.name}
                onClick={() => selectEnterprise(enterprise.id)}
              >
                {enterprise.name}
              </ComboboxOption>
            ))}
          </Combobox>
        </FieldWrap>
      )}

      {actionError && (
        <ContentToast
          type="caution"
          show
          onDismiss={() => setActionError(null)}
        >
          <strong>{t('Error -')}</strong> <code>{actionError.message}</code>
        </ContentToast>
      )}

      {error && !errorDismissed && (
        <ContentToast
          type="caution"
          show
          onDismiss={() => setErrorDismissed(true)}
        >
          <strong>{t('Error loading exports -')}</strong>
          <code>{error.message}</code>
        </ContentToast>
      )}

      {!selectedEnterprise ? (
        <p className={styles.hint}>
          {t('Select an enterprise to view and create security log exports.')}
        </p>
      ) : notConfigured ? (
        <p className={styles.hint}>
          {t('Security log exports are not configured in this environment.')}
        </p>
      ) : (
        <>
          <FieldWrap className={styles.createControls}>
            <TextField
              label={t('Start Date')}
              type="date"
              value={startDate}
              error={startDateError}
              required
              requiredText={t('Required')}
              onChange={(value: string) => setStartDate(value)}
            />
            <TextField
              label={t('End Date')}
              type="date"
              value={endDate}
              error={endDateError}
              required
              requiredText={t('Required')}
              onChange={(value: string) => setEndDate(value)}
            />
            <Button
              variant="primary"
              className={styles.createButton}
              disabled={createInProgress}
              onClick={handleCreate}
            >
              {t('Create Export')}
            </Button>
          </FieldWrap>
          <p className={styles.helperText}>
            {t(
              'Exports are gzip-compressed JSONL (.jsonl.gz) — one event per line.'
            )}
          </p>

          {initialLoading ? (
            <Loader label={t('Loading exports...')} tabIndex={-1} />
          ) : (
            <>
              <SecurityLogExportsTable
                exports={exports}
                onDownload={handleDownload}
                onDelete={handleDelete}
              />

              <Pagination
                className={styles.pagination}
                totalItems={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                statusLabel={
                  <span>
                    {t('Showing')} <strong>{rangeStart}</strong> &ndash;{' '}
                    <strong>{rangeEnd}</strong> {t('of')}{' '}
                    <strong>{totalItems}</strong>
                  </span>
                }
                onNextPageClick={() =>
                  setCurrentPage(p => Math.min(p + 1, totalPages))
                }
                onPreviousPageClick={() =>
                  setCurrentPage(p => Math.max(p - 1, 1))
                }
                onFirstPageClick={() => setCurrentPage(1)}
                onLastPageClick={() => setCurrentPage(totalPages)}
                firstPageLabel={t('First page')}
                lastPageLabel={t('Last page')}
                nextPageLabel={t('Next page')}
                previousPageLabel={t('Previous page')}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default SecurityLogExportsTab;
